import { memo } from "react";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useSessionContext } from "@/contexts/SessionContext";
import { TELECONSULTORIA_AFFILIATIONS_TAB } from "../../affiliation/constants";
import { TeleconsultoriaAffiliationsPanel } from "../../affiliation/components/TeleconsultoriaAffiliationsPanel";
import { useCanManageTeleconsultoriaAffiliations } from "../../affiliation/hooks/useCanManageTeleconsultoriaAffiliations";
import { ClinicActivityView } from "./ClinicActivityView";
import { ClinicAgendasView } from "./ClinicAgendasView";

const ACTIVITY_TAB = "afiliadas";
const AGENDAS_TAB = "agendas";

/**
 * Visão CLÍNICA da prestadora. Seções (em `?tab=`, link direto e refresh):
 * - Teleconsultorias: marcadas, convites e disponibilidade da equipe (todos);
 * - Agendas: por profissional, criar para outro (só gestor: o Form só deixa
 *   escolher outro profissional para quem tem o papel "manager");
 * - Afiliações: gestor/dono.
 */
export const ProviderClinicView = memo(function ProviderClinicView() {
  const { access } = useSessionContext();
  const canManageAgendas = Boolean(access?.roles?.includes("manager"));
  const canManageAffiliations = useCanManageTeleconsultoriaAffiliations();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = [
    { value: ACTIVITY_TAB, label: "Teleconsultorias", isVisible: true },
    { value: AGENDAS_TAB, label: "Agendas", isVisible: canManageAgendas },
    { value: TELECONSULTORIA_AFFILIATIONS_TAB, label: "Afiliações", isVisible: canManageAffiliations },
  ].filter((tab) => tab.isVisible);

  const requestedTab = searchParams.get("tab");
  const currentTab = tabs.some((tab) => tab.value === requestedTab)
    ? (requestedTab as string)
    : ACTIVITY_TAB;

  const handleTabChange = (_: unknown, nextTab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", nextTab);
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <Stack spacing={2} width="100%">
      {tabs.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            aria-label="Seções da visão clínica"
            sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      )}
      {currentTab === ACTIVITY_TAB && <ClinicActivityView />}
      {currentTab === AGENDAS_TAB && <ClinicAgendasView />}
      {currentTab === TELECONSULTORIA_AFFILIATIONS_TAB && <TeleconsultoriaAffiliationsPanel />}
    </Stack>
  );
});
